import React, {ChangeEvent, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {ChangeAuthorView} from 'src/modules/change-author/change-author.view';
import {createEventReviewRoutePath} from 'src/modules/event-review/event-review-route-planner';
import {
    getChangeRequestById,
    updateAuthor,
    updateAuthorFailure,
    updateAuthorSuccess,
} from 'src/openapi/apis/ChangeRequestControllerApi';
import {searchUsers, searchUsersSuccess} from 'src/openapi/apis/UserControllerApi';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestAuthor} from 'src/openapi/models/ChangeRequestAuthor';
import {User} from 'src/openapi/models/User';
import {useAppDispatch} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {useShown} from '@pb/kit-hooks-use-shown';

type Props = {
    changeRequest: ChangeRequest,
};

const MIN_SUGGESTION_LENGTH = 3;
const SUCCESS_AUTO_HIDE_TIMEOUT = 2000;
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const ChangeAuthorController = ({changeRequest}: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {onToggle, shown, onClose} = useShown();
    const {onCreateNote} = usePushNotification();
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [suggestUsers, setSuggestUsers] = useState<Array<User>>([]);
    const [value, setValue] = useState<string>('');
    const changeRequestId = changeRequest.id ?? 0;

    const onSearchUsers = (inputQuery: string) => {
        if (inputQuery.length >= MIN_SUGGESTION_LENGTH) {
            dispatch(searchUsers({searchPhrase: inputQuery})).then(e => {
                if (!e.payload.error && e.type === searchUsersSuccess.type) {
                    setSuggestUsers(e.payload.data);
                }
            });
            return;
        }

        setSuggestUsers([]);
    };

    const onChangeQuery = (event: ChangeEvent<HTMLInputElement>) => {
        const inputQuery = event.target.value;

        setSelectedUser(null);
        setValue(inputQuery);
        onSearchUsers(inputQuery);
    };

    const onItemSelect = (user: User) => {
        setSelectedUser(user);
        setValue(`${user.firstName} ${user.lastName}`);
    };

    const onClear = () => {
        setSuggestUsers([]);
        setSelectedUser(null);
        setValue('');
    };

    const handleClose = () => {
        onClose();
        onClear();
    };

    const onApply = () => {
        if (selectedUser && changeRequestId) {
            const changeRequestAuthor: ChangeRequestAuthor = {
                changeRequestId,
                authorEmail: selectedUser.email,
            };

            dispatch(updateAuthor({changeRequestAuthor})).then(res => {
                if (res.type === updateAuthorSuccess.type) {
                    onCreateNote({
                        id: `${changeRequestId}`,
                        status: Status.SUCCESS,
                        message: 'UPDATED',
                        buttonProps: {iconProps: {type: Icons.CLOSE}},
                        autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
                    });

                    handleClose();
                    dispatch(getChangeRequestById({id: changeRequestId}));
                    navigate(createEventReviewRoutePath({changeRequestId}));
                }

                if (res.payload.error && res.type === updateAuthorFailure.type) {
                    onCreateNote({
                        id: `${changeRequestId}`,
                        status: Status.ERROR,
                        message: res.payload.data.message,
                        buttonProps: {iconProps: {type: Icons.CLOSE}},
                        autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                    });
                }
            });
        }
    };

    return (
        <ChangeAuthorView
            shown={shown}
            value={value}
            selectedUser={selectedUser}
            suggestUsers={suggestUsers}
            changeRequest={changeRequest}
            onChangeQuery={onChangeQuery}
            onItemSelect={onItemSelect}
            onClear={onClear}
            onApply={onApply}
            onToggle={onToggle}
            onClose={handleClose}
        />
    );
};
